/**
 * Sports Betting Types
 */

// Sport category
export interface Sport {
  oddsId: number;
  Id: string;
  name: string;
  nameEn?: string;
  name_kor: string;
  type?: 'sport' | 'esport';
  icon?: string;
}

// Location/Country
export interface Location {
  Id: number;
  name: string;
  name_kor: string;
  image: string;
}

// League
export interface League {
  Id: string;
  name: string;
  name_kor: string;
  sport: {
    Id: string;
    name: string;
    name_kor: string;
    type: string;
    oddsId: number;
  };
  location: Location;
}

// Participant (Team/Player)
export interface Participant {
  id: string;
  name: string;
  nameEn?: string;
  nameKor?: string;
  position: number; // 1 = Home, 2 = Away
  score?: number | null;
}

// Odds for a bet
export interface BetOdds {
  betId: string;
  price: number;
  name: string;
}

// Price from a provider
export interface Price {
  priceId: string;
  providerId: number;
  providerName: string;
  price: number;
  status?: number;
}

// Individual bet option
export interface BetOption {
  betId: string;
  name: string;
  nameKor?: string;
  nameEn?: string;
  status: number;
  participantId?: string;
  prices: Price[];
}

// Market (betting category)
export interface Market {
  marketId: string;
  marketType: string;
  name: string;
  nameEn?: string;
  nameKor?: string;
  baseLine?: number | null;
  eventPartId?: number;
  isClosed: boolean;
  isComplete?: boolean;
  bets: BetOption[];
}

// Event status
export interface EventStatus {
  code: number;
  isLive: boolean;
  isStarted: boolean;
  currentPart?: number | null;
}

// Live score
export interface LiveScore {
  home: number;
  away: number;
  eventDetails?: any[];
}

// Sports event
export interface SportsEvent {
  fixtureId: string;
  gameType: 'prematch' | 'live';
  sport: {
    id: string;
    oddsId: number;
    name: string;
    nameEn?: string;
    nameKor?: string;
    type: string;
  };
  league: {
    id: string;
    name: string;
    nameEn?: string;
    nameKor?: string;
  };
  location: {
    id: number;
    name: string;
    nameEn?: string;
    nameKor?: string;
    flag?: string;
  };
  participants: Participant[];
  startDate: string;
  status: EventStatus;
  odds: {
    home?: BetOdds;
    draw?: BetOdds;
    away?: BetOdds;
  };
  mainMarket?: Market | null;
  allMarkets?: Market[];
  liveScore?: LiveScore | null;
}

// Bet slip item
export interface BetSlipItem {
  id: string;
  event: SportsEvent;
  market: Market;
  bet: BetOption;
  odds: number;
  stake?: number;
  /** ISO timestamp when this selection was added (for odds delay risk management) */
  oddsDisplayedAt?: string;
}

// Individual bet within a multi-bet
export interface BettingHistoryBetItem {
  id: string;
  fixtureId: string;
  sportName: string;
  leagueName: string;
  homeTeam: string;
  awayTeam: string;
  odds: number;
  amount: number;
  potentialWin: number;
  winAmount: number | null;
  betType: string;
  selection: any;
  actualResult?: string | null;
  status: 'pending' | 'won' | 'lost' | 'void' | 'cancelled' | 'cashout';
  eventStartTime: string;
  createdAt: string;
  settledAt: string | null;
}

// Betting history item (can be single bet or multi-bet group)
export interface BettingHistoryItem {
  id: string;
  multiBetId: string | null;
  isMultiBet: boolean;
  betCount: number;
  bets: BettingHistoryBetItem[]; // Array of bets (1 for single, multiple for multi-bet)
  // Summary fields (for display)
  fixtureId: string;
  sportName: string;
  leagueName: string;
  homeTeam: string;
  awayTeam: string;
  odds: number;
  amount: number;
  potentialWin: number;
  winAmount: number | null;
  betType: string;
  selection: any;
  actualResult?: string | null;
  status: 'pending' | 'won' | 'lost' | 'void' | 'cancelled' | 'cashout';
  eventStartTime: string;
  createdAt: string;
  settledAt: string | null;
  isStarted: boolean;
}

// API Response types
export interface SportsApiResponse<T> {
  status: number;
  data?: T;
  message: {
    success?: string;
    warning?: string | null;
  };
}

export interface EventsApiResponse {
  status: number;
  events: SportsEvent[];
  pagination: {
    page: number;
    perPage: number;
    totalPages: number;
    totalCount: number;
  };
  message: {
    success?: string;
    warning?: string | null;
  };
}

// League with events (for grouped response)
export interface LeagueWithEvents {
  leagueId: string;
  leagueName: string;
  leagueNameKor?: string;
  sportId?: string;
  sportName?: string;
  sportNameKor?: string;
  oddsId?: number;
  events: SportsEvent[];
}

// Location with leagues (for grouped response)
export interface LocationWithLeagues {
  locationId: number | string; // Can be number or 'other' string
  locationName: string;
  locationNameKor?: string;
  locationImage?: string | null;
  leagues: LeagueWithEvents[];
}

// Grouped events API response
export interface GroupedEventsApiResponse {
  status: number;
  groupedEvents: LocationWithLeagues[];
  pagination: {
    page: number;
    perPage: number;
    totalPages: number;
    totalCount: number;
  };
  message: {
    success?: string;
    warning?: string | null;
  };
}

export interface PlaceBetRequest {
  fixtureId: string;
  marketId: string;
  betId: string;
  odds: number;
  amount: number;
  gameType?: 'prematch' | 'live'; // Required for correct API fetch (live vs prematch event detail)
  betType?: string;
  selection?: any;
  multiBetId?: string; // For linking multi-bets
  /** ISO timestamp when user first saw these odds (for risk management delay). Use latest of slip items. */
  oddsDisplayedAt?: string;
}

export interface PlaceBetResponse {
  status: number;
  bet?: {
    id: string;
    fixtureId: string;
    marketId: string;
    betId: string;
    odds: number;
    amount: number;
    potentialWin: number;
    status: string;
    multiBetId?: string;
  };
  newBalance?: number;
  message: {
    success?: string;
    warning?: string | null;
  };
}

// Base path for optimized sport icons (frontend/public/optimized/sports/)
const SPORTS_ICON_BASE = '/optimized/sports';

/** Returns emoji string for fallback when no optimized icon exists (name-based or default). */
const getColorfulIconUrl = (emoji: string): string => emoji;

/** Optimized icon path for a sport oddsId. Use SportIcon component to render with emoji fallback. */
export const getSportIconPath = (oddsId: number): string => `${SPORTS_ICON_BASE}/${oddsId}.webp`;

export const SPORT_ICONS: Record<number, string> = {
  1: `${SPORTS_ICON_BASE}/1.webp`,
  3: `${SPORTS_ICON_BASE}/3.webp`,
  5: `${SPORTS_ICON_BASE}/5.webp`,
  7: `${SPORTS_ICON_BASE}/7.webp`,
  9: `${SPORTS_ICON_BASE}/9.webp`,
  11: `${SPORTS_ICON_BASE}/11.webp`,
  12: `${SPORTS_ICON_BASE}/12.webp`,
  13: `${SPORTS_ICON_BASE}/13.webp`,
  14: `${SPORTS_ICON_BASE}/14.webp`,
  17: `${SPORTS_ICON_BASE}/17.webp`,
  19: `${SPORTS_ICON_BASE}/19.webp`,
  21: `${SPORTS_ICON_BASE}/21.webp`,
  22: `${SPORTS_ICON_BASE}/22.webp`,
  24: `${SPORTS_ICON_BASE}/24.webp`,
  26: `${SPORTS_ICON_BASE}/26.webp`,
  27: `${SPORTS_ICON_BASE}/27.webp`,
  29: `${SPORTS_ICON_BASE}/29.webp`,
  31: `${SPORTS_ICON_BASE}/31.webp`,
  33: `${SPORTS_ICON_BASE}/33.webp`,
  35: `${SPORTS_ICON_BASE}/35.webp`,
  63: `${SPORTS_ICON_BASE}/63.webp`,
  91: `${SPORTS_ICON_BASE}/91.webp`,
  92: `${SPORTS_ICON_BASE}/92.webp`,
  110: `${SPORTS_ICON_BASE}/110.webp`,
};

/** Sport name (lowercase) -> oddsId for optimized icons. When API sends name only, use path instead of emoji. */
const SPORT_NAME_TO_ODDS_ID: Record<string, number> = {
  'ice hockey': 13, icehockey: 13, 'ice-hockey': 13, hockey: 13,
  'soccer': 1, football: 1, futsal: 31,
  'tennis': 3,
  'golf': 5,
  'racing': 7, formula: 7, 'formula 1': 7, formula1: 7,
  'baseball': 9,
  'basketball': 11, nba: 11, nba2k: 11, 'nba 2k': 11,
  'volleyball': 12, 'volley ball': 12, 'volley-ball': 12,
  'american football': 5, 'american-football': 5, 'am. football': 5, nfl: 5,
  'rugby': 17,
  'boxing': 19,
  'snooker': 21, pool: 21,
  'table tennis': 63, 'table-tennis': 63, tabletennis: 63, 'ping pong': 63,
  'badminton': 24,
  'winter sports': 26, skiing: 26,
  'cycling': 27,
  'swimming': 29,
  'olympics': 33,
  'mma': 35, 'mixed martial arts': 35, ufc: 35, 'martial arts': 35,
  'cricket': 26,
};

// Esports name-based icon mapping (for when oddsId is not available)
export const ESPORTS_NAME_ICONS: Record<string, string> = {
  'League of Legends': getColorfulIconUrl('🎮'),
  'LoL': getColorfulIconUrl('🎮'),
  'League': getColorfulIconUrl('🎮'),
  'CS:GO': getColorfulIconUrl('🔫'),
  'Counter-Strike': getColorfulIconUrl('🔫'),
  'CS2': getColorfulIconUrl('🔫'),
  'Counter Strike': getColorfulIconUrl('🔫'),
  'Dota 2': getColorfulIconUrl('⚔️'),
  'Dota': getColorfulIconUrl('⚔️'),
  'Valorant': getColorfulIconUrl('🎯'),
  'Overwatch': getColorfulIconUrl('🦸'),
  'Call of Duty': getColorfulIconUrl('💣'),
  'CoD': getColorfulIconUrl('💣'),
  'Rainbow Six': getColorfulIconUrl('🌈'),
  'Rainbow Six Siege': getColorfulIconUrl('🌈'),
  'Rocket League': getColorfulIconUrl('🚗'),
  'FIFA': getColorfulIconUrl('⚽'),
  'EA FC': getColorfulIconUrl('⚽'),
  'EA Sports FC': getColorfulIconUrl('⚽'),
  'NBA 2K': getColorfulIconUrl('🏀'),
  'NBA2K': getColorfulIconUrl('🏀'),
  'NBA2k': getColorfulIconUrl('🏀'),
  'NBA 2k': getColorfulIconUrl('🏀'),
  'Starcraft': getColorfulIconUrl('👾'),
  'StarCraft': getColorfulIconUrl('👾'),
  'StarCraft II': getColorfulIconUrl('👾'),
  'Hearthstone': getColorfulIconUrl('🃏'),
  'PUBG': getColorfulIconUrl('🪖'),
  'PlayerUnknown\'s Battlegrounds': getColorfulIconUrl('🪖'),
  'Fortnite': getColorfulIconUrl('🏗️'),
  'Apex Legends': getColorfulIconUrl('🦊'),
  'Mobile Legends': getColorfulIconUrl('📱'),
  'MLBB': getColorfulIconUrl('📱'),
  'King of Glory': getColorfulIconUrl('👑'),
  'Wild Rift': getColorfulIconUrl('🌊'),
  'Arena of Valor': getColorfulIconUrl('👑'),
};

// Get sport icon helper - tries oddsId first, then name for esports
export const getSportIcon = (oddsId: number | string | undefined | null, sportName?: string): string => {
  // Normalize inputs
  const normalizedOddsId = oddsId === null || oddsId === undefined || oddsId === '' ? undefined : oddsId;
  const normalizedName = sportName?.trim();
  
  // Priority 1: If oddsId is provided and is a valid number (not 0, null, or undefined), use it directly
  if (typeof normalizedOddsId === 'number' && normalizedOddsId > 0) {
    const icon = SPORT_ICONS[normalizedOddsId];
    if (icon) {
      return icon;
    }
  }
  
  // Priority 2: If oddsId is a string, try to parse it
  if (typeof normalizedOddsId === 'string' && normalizedOddsId !== '0' && normalizedOddsId !== 'null' && normalizedOddsId !== 'undefined') {
    const parsed = parseInt(normalizedOddsId, 10);
    if (!isNaN(parsed) && parsed > 0) {
      const icon = SPORT_ICONS[parsed];
      if (icon) {
        return icon;
      }
    }
  }
  
  // Priority 3: For esports or when oddsId doesn't match, try name-based matching
  // Only use name matching if oddsId is not available or doesn't match
  if (normalizedName) {
    const nameLower = normalizedName.toLowerCase();
    
    // Name -> optimized icon path (so ice hockey, hockey, etc. use 13.webp not emoji)
    for (const [key, id] of Object.entries(SPORT_NAME_TO_ODDS_ID)) {
      if (nameLower === key) {
        const path = SPORT_ICONS[id];
        if (path) return path;
        break;
      }
    }
    
    // First check for esports games (more specific matches first)
    for (const [key, icon] of Object.entries(ESPORTS_NAME_ICONS)) {
      const keyLower = key.toLowerCase();
      // Check exact match first,
      if (nameLower === keyLower) {
        return icon;
      }
    }
    
    // Then check for regular sports by name (only if oddsId wasn't found)
    // Comprehensive sport name mapping with all variations
    const sportNameMap: Record<string, string> = {
      // Ice Hockey - check first before generic "hockey"
      'ice hockey': getColorfulIconUrl('🏒'),
      'icehockey': getColorfulIconUrl('🏒'),
      'ice-hockey': getColorfulIconUrl('🏒'),
      // Basketball - all variations
      'basketball': getColorfulIconUrl('🏀'),
      'basket ball': getColorfulIconUrl('🏀'),
      'nba': getColorfulIconUrl('🏀'),
      'nba2k': getColorfulIconUrl('🏀'),
      'nba 2k': getColorfulIconUrl('🏀'),
      'nba 2K': getColorfulIconUrl('🏀'),
      'nba2K': getColorfulIconUrl('🏀'),
      // Volleyball
      'volleyball': getColorfulIconUrl('🏐'),
      'volley ball': getColorfulIconUrl('🏐'),
      'volley-ball': getColorfulIconUrl('🏐'),
      // MMA - all variations
      'mma': getColorfulIconUrl('🥋'),
      'mixed martial arts': getColorfulIconUrl('🥋'),
      'mixed-martial-arts': getColorfulIconUrl('🥋'),
      'ufc': getColorfulIconUrl('🥋'),
      'martial arts': getColorfulIconUrl('🥋'),
      'martial-arts': getColorfulIconUrl('🥋'),
      // American Football
      'american football': getColorfulIconUrl('🏈'),
      'american-football': getColorfulIconUrl('🏈'),
      'am. football': getColorfulIconUrl('🏈'),
      'am football': getColorfulIconUrl('🏈'),
      'nfl': getColorfulIconUrl('🏈'),
      // Table Tennis
      'table tennis': getColorfulIconUrl('🏓'),
      'table-tennis': getColorfulIconUrl('🏓'),
      'tabletennis': getColorfulIconUrl('🏓'),
      'ping pong': getColorfulIconUrl('🏓'),
      'ping-pong': getColorfulIconUrl('🏓'),
      'pingpong': getColorfulIconUrl('🏓'),
      // Other sports
      'soccer': getColorfulIconUrl('⚽'),
      'football': getColorfulIconUrl('⚽'), // Regular football/soccer (after american football)
      'tennis': getColorfulIconUrl('🎾'),
      'golf': getColorfulIconUrl('⛳'),
      'racing': getColorfulIconUrl('🏎️'),
      'formula': getColorfulIconUrl('🏎️'),
      'formula 1': getColorfulIconUrl('🏎️'),
      'formula1': getColorfulIconUrl('🏎️'),
      'baseball': getColorfulIconUrl('⚾'),
      'hockey': getColorfulIconUrl('🏒'), // Generic hockey (after ice hockey)
      'rugby': getColorfulIconUrl('🏉'),
      'boxing': getColorfulIconUrl('🥊'),
      'snooker': getColorfulIconUrl('🎱'),
      'pool': getColorfulIconUrl('🎱'),
      'badminton': getColorfulIconUrl('🏸'),
      'winter sports': getColorfulIconUrl('⛷️'),
      'skiing': getColorfulIconUrl('⛷️'),
      'cycling': getColorfulIconUrl('🚴'),
      'swimming': getColorfulIconUrl('🏊'),
      'futsal': getColorfulIconUrl('⚽'),
      'olympics': getColorfulIconUrl('🎖️'),
      'cricket': getColorfulIconUrl('🏏'),
    };
    
    // Check for exact matches first
    if (sportNameMap[nameLower]) {
      return sportNameMap[nameLower];
    }
    
    // Then check for partial matches (name contains key or key contains name)
    for (const [key, icon] of Object.entries(sportNameMap)) {
      const keyLower = key.toLowerCase();
      if (nameLower.includes(keyLower) || keyLower.includes(nameLower)) {
        return icon;
      }
    }
  }
  
  // Default fallback
  return getColorfulIconUrl('🏆');
};

// Fixture status labels
export const FIXTURE_STATUS: Record<number, string> = {
  0: 'Closed',
  1: 'Upcoming',
  2: 'In Progress',
  3: 'Finished',
  4: 'Interrupted',
  5: 'Cancelled',
  6: 'Walkover',
  7: 'Abandoned',
  8: 'Retired',
};

// Bet status labels
export const BET_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'yellow' },
  won: { label: 'Won', color: 'green' },
  lost: { label: 'Lost', color: 'red' },
  void: { label: 'Void', color: 'gray' },
  cancelled: { label: 'Cancelled', color: 'gray' },
  cashout: { label: 'Cashed Out', color: 'blue' },
};
